import * as React from "react";

import { cn } from "@/lib/cn";
import { Input, type InputProps } from "@/components/ui/input";

/**
 * MoneyInput
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.4
 *
 * A thin wrapper around `<Input>` that:
 *   - Right-aligns numeric content (cash columns read better right-aligned).
 *   - Renders a currency symbol prefix in a muted-foreground span absolutely
 *     positioned inside the input.
 *   - Uses `inputMode="decimal"` so mobile keyboards default to the numeric
 *     pad with a period.
 *   - Coerces the value to a string with two decimals when typed-numeric.
 *
 * It does NOT do currency-format-on-blur — keep the input raw and format on
 * display only (e.g. via `Intl.NumberFormat` in InfoField). That avoids the
 * "value jumps when I tab away" problem.
 *
 * For multi-currency lists, pass `symbol` per-instance (e.g. `"€"`,
 * `"CA$"`). Defaults to `$`.
 */
export interface MoneyInputProps extends Omit<InputProps, "type"> {
  /** Currency symbol shown as a prefix. Default `$`. */
  symbol?: string;
}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ className, symbol = "$", ...rest }, ref) => {
    return (
      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs text-muted-foreground"
        >
          {symbol}
        </span>
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          // Step is irrelevant for text input but documents intent.
          step="0.01"
          className={cn("pl-7 text-right tabular-nums", className)}
          {...rest}
        />
      </div>
    );
  }
);
MoneyInput.displayName = "MoneyInput";
